/**
 * Satu loop requestAnimationFrame bersama untuk semua orb di halaman.
 *
 * Alasannya operasional: halaman admin bisa menampilkan beberapa orb sekaligus
 * (kartu varian + pratinjau + FAB), dan kiosk `/wall` menyala 24 jam — ~5 juta
 * frame per hari. Satu loop yang membagikan waktu jauh lebih murah daripada
 * satu loop per komponen, dan ia berhenti total saat pelanggan terakhir lepas.
 */

export interface OrbLoopSubscriber {
	/** Batas frame per detik untuk pelanggan ini. */
	fps: number;
	/** Dipanggil dengan waktu milidetik sejak pelanggan mendaftar. */
	draw(elapsedMs: number): void;
}

interface Entry {
	subscriber: OrbLoopSubscriber;
	startedAt: number;
	lastDrawAt: number;
}

const entries = new Set<Entry>();
let frameHandle: number | null = null;

const now = (): number =>
	typeof performance !== "undefined" ? performance.now() : Date.now();

/** Tab tersembunyi: browser sudah melambatkan rAF, tapi ini menghentikannya. */
const isPageVisible = (): boolean =>
	typeof document === "undefined" || document.visibilityState !== "hidden";

function tick(): void {
	frameHandle = null;
	if (entries.size === 0) return;

	if (isPageVisible()) {
		const timestamp = now();
		for (const entry of entries) {
			const minInterval = 1000 / Math.max(1, entry.subscriber.fps);
			if (timestamp - entry.lastDrawAt < minInterval) continue;
			entry.lastDrawAt = timestamp;
			entry.subscriber.draw(timestamp - entry.startedAt);
		}
	}
	schedule();
}

function schedule(): void {
	if (frameHandle !== null || entries.size === 0) return;
	if (typeof requestAnimationFrame !== "function") return;
	frameHandle = requestAnimationFrame(tick);
}

/**
 * Mendaftarkan satu orb ke loop bersama. Mengembalikan fungsi pembatalan —
 * panggil saat komponen dilepas, kalau tidak loop tidak akan pernah berhenti.
 */
export function subscribeToOrbLoop(subscriber: OrbLoopSubscriber): () => void {
	const timestamp = now();
	const entry: Entry = {
		subscriber,
		startedAt: timestamp,
		// Mundur satu interval penuh supaya frame pertama langsung digambar.
		lastDrawAt: Number.NEGATIVE_INFINITY,
	};
	entries.add(entry);
	schedule();
	return () => {
		entries.delete(entry);
		if (entries.size === 0 && frameHandle !== null) {
			if (typeof cancelAnimationFrame === "function")
				cancelAnimationFrame(frameHandle);
			frameHandle = null;
		}
	};
}

/** Jumlah orb yang sedang dianimasikan — dipakai test, bukan kode produksi. */
export function orbLoopSize(): number {
	return entries.size;
}
